import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Inline, Stack, xcss, Box } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { SpotlightTarget } from '@atlaskit/onboarding';

import {
	GeneralAccessLevelSelector,
	type GeneralAccessLevelSelectorProps,
} from './GeneralAccessLevelSelector';
import {
	GeneralAccessPermissionDropdown,
	type GeneralAccessPermissionDropdownProps,
} from './GeneralAccessPermissionDropdown';

const i18n = defineMessages({
	generalAccessTitle: {
		id: 'share-and-restrict-dialog.general-access.title',
		defaultMessage: 'General access',
		description:
			'Section heading for controls that determine whether non-specific users are able to access this content',
	},
});

export type GeneralAccessProps = {
	generalAccessLevelSelectorProps: GeneralAccessLevelSelectorProps;
	generalAccessPermissionDropdownProps: GeneralAccessPermissionDropdownProps | undefined;
};

const generalAccessSectionContainer = xcss({
	paddingTop: 'space.100',
});

const bottomPadding = xcss({
	paddingBlockEnd: 'space.100',
});

const levelSelectorSpotLightContainer = xcss({
	padding: 'space.075',
	marginInlineStart: 'space.negative.075',
});

const dropdownSpotLightContainer = xcss({
	padding: 'space.075',
	marginInlineEnd: 'space.negative.075',
});

export const GeneralAccess = ({
	generalAccessLevelSelectorProps,
	generalAccessPermissionDropdownProps,
	showBottomPadding,
}: GeneralAccessProps & { showBottomPadding: boolean }) => {
	const { formatMessage } = useIntl();
	return (
		<Stack grow="fill">
			<Heading size="xxsmall" as="h2">
				{formatMessage(i18n.generalAccessTitle)}
			</Heading>
			<Inline
				grow="fill"
				spread="space-between"
				xcss={[generalAccessSectionContainer, showBottomPadding && bottomPadding]}
			>
				<SpotlightTarget name="generalAccess">
					<Box xcss={levelSelectorSpotLightContainer}>
						<GeneralAccessLevelSelector {...generalAccessLevelSelectorProps} />
					</Box>
				</SpotlightTarget>
				{generalAccessPermissionDropdownProps && (
					<SpotlightTarget name="permissionDropdown">
						<Box xcss={dropdownSpotLightContainer}>
							<GeneralAccessPermissionDropdown {...generalAccessPermissionDropdownProps} />
						</Box>
					</SpotlightTarget>
				)}
			</Inline>
		</Stack>
	);
};
